import { computed, onBeforeUnmount, reactive, watch, type ComputedRef, type Ref } from 'vue'

/**
 * Open kit overlays (modals, drawers, confirm dialogs) in opening order. PrimeVue binds Escape on the document for every
 * open Dialog/Drawer, so one key press would close a nested modal **and** the one under it (the interview modal over
 * the status-change modal, R4; a confirm over an edit modal). Kit overlays keep PrimeVue's `close-on-escape` off and
 * register here instead: only the top overlay reacts to Escape.
 */
const stack = reactive<symbol[]>([])

export function isTopOverlay(id: symbol): boolean {
  return stack[stack.length - 1] === id
}

/**
 * Puts an overlay on top of the stack; `onEscape` runs when Escape is pressed while it is the top one (the key press is
 * then marked handled, so no other listener closes the next overlay with it). Returns the function that removes it.
 */
export function openOverlay(id: symbol, onEscape?: () => void): () => void {
  closeOverlay(id)
  stack.push(id)
  const onKeydown = (event: KeyboardEvent) => {
    if ((event.key !== 'Escape' && event.code !== 'Escape') || event.defaultPrevented || !isTopOverlay(id)) return
    event.preventDefault()
    onEscape?.()
  }
  if (onEscape && typeof document !== 'undefined') document.addEventListener('keydown', onKeydown)
  listeners.set(id, onKeydown)
  return () => closeOverlay(id)
}

const listeners = new Map<symbol, (event: KeyboardEvent) => void>()

export function closeOverlay(id: symbol): void {
  const index = stack.indexOf(id)
  if (index !== -1) stack.splice(index, 1)
  const listener = listeners.get(id)
  if (listener && typeof document !== 'undefined') document.removeEventListener('keydown', listener)
  listeners.delete(id)
}

/** Registers a component's overlay while `open` is true (see `openOverlay`). */
export function useOverlayStack(
  open: Ref<boolean>,
  options: { onEscape?: () => void } = {},
): { isTop: ComputedRef<boolean>; depth: ComputedRef<number> } {
  const id = Symbol('overlay')
  watch(open, (value) => (value ? openOverlay(id, options.onEscape) : closeOverlay(id)), { immediate: true })
  onBeforeUnmount(() => closeOverlay(id))
  return {
    isTop: computed(() => stack[stack.length - 1] === id),
    depth: computed(() => stack.indexOf(id)),
  }
}
